import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminMenuItems, groupMenuItems } from './adminMenu';
import apiService from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [securityMenuOpen, setSecurityMenuOpen] = useState(false);
  // Menu groups the user folded; remembered in this browser only.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => {
    try {
      const saved = window.localStorage.getItem('admin-menu-closed-groups');
      return new Set<string>(saved ? JSON.parse(saved) : ['Tienda web', 'Sistema']);
    } catch {
      return new Set<string>(['Tienda web', 'Sistema']);
    }
  });
  const toggleGroup = (title: string) => setClosedGroups((current) => {
    const next = new Set(current);
    if (next.has(title)) next.delete(title); else next.add(title);
    try { window.localStorage.setItem('admin-menu-closed-groups', JSON.stringify(Array.from(next))); } catch { /* sin almacenamiento: solo dura la sesión */ }
    return next;
  });
  const [pendingMessages, setPendingMessages] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useBodyScrollLock(sidebarOpen);
  useEscapeToClose(sidebarOpen, () => setSidebarOpen(false));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Un admin restringido a ciertos clientes (allowedClientIds) solo ve las
  // secciones relacionadas a esos clientes en el menu.
  const isRestricted = Array.isArray(user?.allowedClientIds);

  const menuItems = adminMenuItems(user).map((item) => ({ ...item, icon: <item.Icon className="h-5 w-5" /> }));
  const menuGroups = groupMenuItems(menuItems);
  const securityItems = [
    { path: '/admin/seguridad/resumen', label: 'Resumen' },
    { path: '/admin/seguridad/analitica', label: 'Analítica web' },
    { path: '/admin/seguridad/backups', label: 'Backups' },
    { path: '/admin/seguridad/eventos', label: 'Eventos' },
    { path: '/admin/seguridad/actividad', label: 'Actividad de administradores' }
  ];

  useEffect(() => {
    let mounted = true;
    const fetchPendingMessages = async () => {
      try {
        const response = await apiService.getContactMessages({ status: 'nuevo', page: 1, limit: 1 });
        if (!mounted) return;
        setPendingMessages(Number(response.total || 0));
      } catch {
        if (!mounted) return;
        setPendingMessages(0);
      }
    };

    fetchPendingMessages();
    const interval = window.setInterval(fetchPendingMessages, 30000);
    const handleMessagesUpdated = () => {
      fetchPendingMessages();
    };
    window.addEventListener('admin:messages-updated', handleMessagesUpdated);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener('admin:messages-updated', handleMessagesUpdated);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchPendingOrders = async () => {
      try {
        const response = await apiService.getOrders({ status: 'pendiente', page: 1, limit: 1 });
        if (mounted) setPendingOrders(Number(response.total || 0));
      } catch {
        if (mounted) setPendingOrders(0);
      }
    };
    fetchPendingOrders();
    const interval = window.setInterval(fetchPendingOrders, 30000);
    window.addEventListener('admin:orders-updated', fetchPendingOrders);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener('admin:orders-updated', fetchPendingOrders);
    };
  }, []);
  const isActive = (path: string, exact: boolean = false) => {
    if (exact) return location.pathname === path;
    // Whole segments only: "/admin/pedidos" must not match "/admin/pedidos-mensuales".
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const currentSectionLabel = menuItems.find((item) => isActive(item.path, item.exact))?.label || 'Inicio';

  // Sin esto la pestaña queda con el título de la pantalla anterior (por ejemplo "Iniciar Sesión").
  useEffect(() => {
    document.title = `${currentSectionLabel} - Plásticos Polcen`;
  }, [currentSectionLabel]);

  return (
    <div className="min-h-screen bg-slate-100 lg:flex">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[1px]"></div>
        </div>
      )}

      <div className={`fixed inset-y-0 left-0 z-50 flex h-full w-72 flex-col bg-white/95 backdrop-blur border-r border-slate-200 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:h-screen ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <div className="flex items-center">
            <img
              src="/images/logo.jpg"
              alt="Plásticos Polcen"
              className="h-9 w-9 rounded-xl object-cover shadow-sm"
            />
            <div className="ml-3">
              <span className="block text-base font-semibold text-slate-900 leading-none">Panel Admin</span>
              <span className="block text-xs text-slate-500 mt-1">Plásticos Polcen</span>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 rounded-md text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <span className="text-primary-700 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{isRestricted ? 'Acceso limitado a clientes' : 'Administrador'}</p>
            </div>
          </div>
        </div>

        <nav className="mt-6 px-3 flex-1 overflow-y-auto pb-4">
          <div className="space-y-3">
            {menuGroups.map((group) => {
              // The group of the open section never stays folded, so the active item is always visible.
              const folded = !!group.title && closedGroups.has(group.title) && !group.items.some((item) => isActive(item.path, item.exact));
              return (
              <div key={group.title || 'inicio'}>
                {group.title && (
                  <button type="button" onClick={() => toggleGroup(group.title)} aria-expanded={!folded} className="flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-200">
                    {group.title}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${folded ? '-rotate-90' : ''}`} />
                  </button>
                )}
                {!folded && <div className="space-y-1">
            {group.items.map((item) => item.path === '/admin/seguridad' ? (
              <button
                key={item.path}
                onClick={() => setSecurityMenuOpen(open => !open)}
                className={`w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-200 ${isActive(item.path) ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                {item.icon}
                <span className="ml-3">{item.label}</span>
                <ChevronRight className={`ml-auto h-4 w-4 transition-transform ${securityMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => { setSidebarOpen(false); setSecurityMenuOpen(false); }}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all ${isActive(item.path, item.exact) ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                {item.icon}
                <span className="ml-3">{item.label}</span>
                {item.path === '/admin/pedidos' && pendingOrders > 0 && (
                  <span className="ml-auto inline-flex min-w-[1.25rem] h-5 px-1.5 items-center justify-center rounded-full bg-rose-600 text-white text-xs font-semibold shadow-sm" title={`${pendingOrders} pedido(s) pendiente(s)`}>
                    {pendingOrders > 99 ? '99+' : pendingOrders}
                  </span>
                )}
                {isActive(item.path, item.exact) && <ChevronRight className={`${item.path === '/admin/pedidos' && pendingOrders > 0 ? 'ml-2' : 'ml-auto'} h-4 w-4 opacity-90`} />}
                {item.path === '/admin/mensajes' && pendingMessages > 0 && (
                  <span className="ml-auto inline-flex min-w-[1.25rem] h-5 px-1.5 items-center justify-center rounded-full bg-rose-600 text-white text-xs font-semibold">
                    {pendingMessages > 99 ? '99+' : pendingMessages}
                  </span>
                )}
              </Link>
            ))}
                </div>}
              </div>
              );
            })}
          </div>
        </nav>

        {securityMenuOpen && user?.role === 'superadmin' && <aside className="fixed left-0 lg:left-72 top-0 z-[60] h-screen w-72 lg:w-60 bg-white text-slate-800 shadow-2xl border-l border-r border-slate-200">
          <div className="h-16 px-5 flex items-center justify-between border-b border-slate-200">
            <div><p className="font-semibold text-slate-900">Seguridad</p><p className="text-xs text-slate-500">Seleccioná un módulo</p></div>
            <button onClick={() => setSecurityMenuOpen(false)} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none"><X className="h-5 w-5" /></button>
          </div>
          <nav className="p-3 space-y-1">
            {securityItems.map(item => <Link key={item.path} to={item.path} onClick={() => { setSecurityMenuOpen(false); setSidebarOpen(false); }} className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${location.pathname === item.path ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}>
              <span>{item.label}</span><ChevronRight className="h-4 w-4" />
            </Link>)}
          </nav>
        </aside>}

        <div className="p-3 border-t border-slate-200 bg-white">
          <button onClick={handleLogout} className="w-full flex items-center px-3 py-2 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-100 hover:text-slate-900 transition-colors">
            <LogOut className="h-5 w-5" />
            <span className="ml-3">Cerrar Sesion</span>
          </button>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between gap-2 h-16 px-4 sm:px-6 lg:px-8">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-md text-slate-400 hover:text-slate-600">
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex min-w-0 items-center space-x-4">
              <span className="truncate text-sm font-semibold text-slate-800 sm:hidden">{currentSectionLabel}</span>
              <div className="hidden sm:block text-sm text-slate-500">
                Sección: <span className="font-semibold text-slate-800">{currentSectionLabel}</span>
              </div>
              <div className="hidden md:block text-sm text-slate-500">Bienvenido, <span className="font-medium text-slate-900">{user?.name}</span></div>
            </div>
            <Link
              to="/"
              className="inline-flex shrink-0 items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
              title="Ir al sitio"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Ir al sitio</span>
            </Link>
          </div>
        </div>

        <main className="p-3 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
